import { type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type CommentAnchor,
  type CommentMarkerViewModel,
  type GraphicBoundsResolver,
  type PointDefinition,
} from '@bizzdesign/modeler/models';
import {
  type Comment,
  type CommentCoordinates,
  type CommentCreatedPayload,
  type CommentEvent,
  type CommentParent,
  type CommentReaction,
  type CommentsDeletedPayload,
  type CommentThread,
  type CommentUser,
} from '@bizzdesign/sdk-chat';

/** Threads of the current parent, keyed by thread id. */
export type CommentThreadMap = Readonly<Record<string, CommentThread>>;

/**
 * Converts threads to a map keyed by thread id.
 *
 * @param threads - Threads to index.
 * @returns The threads keyed by id.
 */
export function toThreadMap(
  threads: readonly CommentThread[],
): CommentThreadMap {
  return Object.fromEntries(threads.map((thread) => [thread.id, thread]));
}

/**
 * Converts a marker anchor into the coordinates stored on a thread.
 *
 * Convention: when the anchor has a graphic, the stored coordinates are the
 * offset of the marker from the graphic's top-left corner; otherwise they are
 * absolute diagram coordinates. When the graphic cannot be found the anchor
 * falls back to an unattached marker so the stored position stays meaningful.
 *
 * @param anchor - Where the marker was placed.
 * @param resolveBounds - Current bounds of graphics on the canvas.
 * @returns The coordinates and graphic id to send to the API.
 */
export function toStoredAnchor(
  anchor: CommentAnchor,
  resolveBounds: GraphicBoundsResolver | null,
): { coordinates: CommentCoordinates; graphicId: UUID | undefined } {
  const bounds =
    anchor.graphicId === undefined
      ? undefined
      : resolveBounds?.(anchor.graphicId);
  if (anchor.graphicId === undefined || bounds === undefined) {
    return {
      coordinates: { x: anchor.worldPoint.x, y: anchor.worldPoint.y },
      graphicId: undefined,
    };
  }
  return {
    coordinates: {
      x: anchor.worldPoint.x - bounds.x,
      y: anchor.worldPoint.y - bounds.y,
    },
    graphicId: anchor.graphicId,
  };
}

/**
 * Resolves where a thread's marker sits on the canvas right now.
 *
 * @param thread - Thread carrying stored coordinates and an optional graphic id.
 * @param resolveBounds - Current bounds of graphics on the canvas.
 * @returns The world position and whether the thread lost its graphic.
 */
export function resolveMarkerPosition(
  thread: Pick<CommentThread, 'coordinates' | 'graphicId'>,
  resolveBounds: GraphicBoundsResolver | null,
): { worldPosition: PointDefinition; isDetached: boolean } {
  if (thread.graphicId === undefined) {
    return {
      worldPosition: { x: thread.coordinates.x, y: thread.coordinates.y },
      isDetached: false,
    };
  }
  const bounds = resolveBounds?.(thread.graphicId);
  if (bounds === undefined) {
    // The graphic is gone; the offset can only be shown from the diagram origin.
    return {
      worldPosition: { x: thread.coordinates.x, y: thread.coordinates.y },
      isDetached: true,
    };
  }
  return {
    worldPosition: {
      x: bounds.x + thread.coordinates.x,
      y: bounds.y + thread.coordinates.y,
    },
    isDetached: false,
  };
}

/**
 * Whether the thread holds a comment the user has not read and did not write.
 *
 * @param thread - Thread to inspect.
 * @param userId - Current user, or `null` when unknown.
 * @returns `true` when at least one comment is unread for the user.
 */
export function hasUnreadComments(
  thread: CommentThread,
  userId: UUID | null,
): boolean {
  if (userId === null) {
    return false;
  }
  return thread.comments.some(
    (comment) =>
      comment.author.id !== userId && !(comment.readBy ?? []).includes(userId),
  );
}

/** Oldest comment of a thread by creation time (its root), or `undefined` for an empty thread. */
export function rootCommentOf(thread: CommentThread): Comment | undefined {
  return thread.comments.reduce<Comment | undefined>(
    (oldest, comment) =>
      oldest === undefined ||
      comment.systemAttributes.createdAt < oldest.systemAttributes.createdAt
        ? comment
        : oldest,
    undefined,
  );
}

/** Newest comment of a thread by creation time, or `undefined` for an empty thread. */
export function lastCommentOf(thread: CommentThread): Comment | undefined {
  return thread.comments.reduce<Comment | undefined>(
    (latest, comment) =>
      latest === undefined ||
      comment.systemAttributes.createdAt > latest.systemAttributes.createdAt
        ? comment
        : latest,
    undefined,
  );
}

/**
 * Builds the render model of every thread.
 *
 * @param threads - Threads of the current parent.
 * @param resolveBounds - Current bounds of graphics on the canvas.
 * @param userId - Current user, for the unread state.
 * @param selectedThreadId - Thread whose panel is open.
 * @returns One marker per thread, in creation order.
 */
export function toMarkerViewModels(
  threads: CommentThreadMap,
  resolveBounds: GraphicBoundsResolver | null,
  userId: UUID | null,
  selectedThreadId: UUID | null,
): CommentMarkerViewModel[] {
  return sortByCreation(Object.values(threads)).map((thread) => {
    const { worldPosition, isDetached } = resolveMarkerPosition(
      thread,
      resolveBounds,
    );
    return {
      threadId: thread.id,
      worldPosition,
      ...(thread.graphicId === undefined
        ? {}
        : { graphicId: thread.graphicId }),
      isDetached,
      isResolved: thread.resolved,
      commentCount: thread.comments.length,
      lastComment: lastCommentOf(thread),
      hasUnread: hasUnreadComments(thread, userId),
      isSelected: thread.id === selectedThreadId,
    };
  });
}

/** Sorts threads oldest first by their creation time. */
export function sortByCreation(threads: CommentThread[]): CommentThread[] {
  return [...threads].sort(
    (a, b) =>
      a.systemAttributes.createdAt.getTime() -
      b.systemAttributes.createdAt.getTime(),
  );
}

/**
 * Inserts or replaces a comment inside a thread, keeping comments ordered by
 * creation time.
 */
export function upsertComment(
  thread: CommentThread,
  comment: Comment,
): CommentThread {
  const others = thread.comments.filter((c) => c.id !== comment.id);
  const comments = [...others, comment].sort(
    (a, b) =>
      a.systemAttributes.createdAt.getTime() -
      b.systemAttributes.createdAt.getTime(),
  );
  return { ...thread, comments };
}

/** Inserts or replaces a thread in the map. */
export function upsertThread(
  threads: CommentThreadMap,
  thread: CommentThread,
): CommentThreadMap {
  return { ...threads, [thread.id]: thread };
}

/**
 * Merges a thread returned by a mutation into the thread currently in the
 * store. A mutation response is a snapshot taken when the server handled the
 * request; realtime events applied since then may be newer, so neither side
 * may overwrite the other blindly.
 *
 * Rules: thread-level fields come from whichever side has the later
 * `systemAttributes.updatedAt`; comments are merged by id, the later
 * `updatedAt` wins per comment, and comments known only to one side are kept.
 *
 * @param current - Thread in the store, or `undefined` when it is new.
 * @param incoming - Thread from the mutation response.
 * @returns The merged thread.
 */
export function mergeThread(
  current: CommentThread | undefined,
  incoming: CommentThread,
): CommentThread {
  if (current === undefined) {
    return incoming;
  }
  const base =
    current.systemAttributes.updatedAt > incoming.systemAttributes.updatedAt
      ? current
      : incoming;
  const byId = new Map<string, Comment>(
    current.comments.map((comment) => [comment.id, comment]),
  );
  for (const comment of incoming.comments) {
    const known = byId.get(comment.id);
    if (
      known === undefined ||
      comment.systemAttributes.updatedAt >= known.systemAttributes.updatedAt
    ) {
      byId.set(comment.id, comment);
    }
  }
  const comments = [...byId.values()].sort(
    (a, b) =>
      a.systemAttributes.createdAt.getTime() -
      b.systemAttributes.createdAt.getTime(),
  );
  return { ...base, comments };
}

/** Removes threads from the map; returns the same map when nothing changed. */
export function removeThreads(
  threads: CommentThreadMap,
  ids: readonly string[],
): CommentThreadMap {
  if (!ids.some((id) => id in threads)) {
    return threads;
  }
  const next = { ...threads };
  ids.forEach((id) => delete next[id]);
  return next;
}

/**
 * Applies one realtime event to the thread map.
 *
 * Filtering rule: a `CREATE_COMMENT` for a known thread is a reply and is
 * always applied; a `CREATE_COMMENT` for an unknown thread is a new thread and
 * is kept only when it belongs to `parent` (reply events carry no parent id).
 * Every other event only carries a thread id and is applied when the thread is
 * known. Ignored events return the input map unchanged.
 *
 * @param threads - Current threads of the parent.
 * @param event - Realtime event from the chat subscription.
 * @param parent - Frame or view the store is connected to.
 * @returns The updated map, or the same instance when the event was ignored.
 */
export function applyCommentEvent(
  threads: CommentThreadMap,
  event: CommentEvent,
  parent: CommentParent,
): CommentThreadMap {
  switch (event.type) {
    case 'CREATE_COMMENT':
      return applyCommentCreated(threads, event.payload, parent);
    case 'UPDATE_COMMENT':
      return updateKnownThread(threads, event.payload.threadId, (thread) => ({
        ...thread,
        comments: patchComment(thread.comments, event.payload.commentId, {
          text: event.payload.text,
          systemAttributes: event.payload.systemAttributes,
        }),
      }));
    case 'DELETE_COMMENT':
      return applyCommentsDeleted(threads, event.payload);
    case 'UPDATE_COMMENT_THREAD':
      return updateKnownThread(threads, event.payload.threadId, (thread) => ({
        ...thread,
        coordinates: event.payload.coordinates ?? thread.coordinates,
        ...(event.payload.graphicId === undefined
          ? {}
          : { graphicId: event.payload.graphicId }),
        systemAttributes:
          event.payload.threadSystemAttributes ?? thread.systemAttributes,
        comments: patchComment(thread.comments, event.payload.commentId, {
          text: event.payload.text,
          systemAttributes: event.payload.commentSystemAttributes,
        }),
      }));
    case 'RESOLVE_COMMENT_THREAD':
      return updateKnownThread(threads, event.payload.threadId, (thread) => {
        // Drop the old timestamp so a reopened thread carries no resolution metadata.
        const { resolvedAt: _previousResolvedAt, ...rest } = thread;
        return {
          ...rest,
          resolved: event.payload.resolved,
          ...(event.payload.resolvedAt === undefined
            ? {}
            : { resolvedAt: event.payload.resolvedAt }),
          systemAttributes:
            event.payload.systemAttributes ?? thread.systemAttributes,
        };
      });
    case 'DELETE_COMMENT_THREAD':
      return removeThreads(threads, [event.payload.threadId]);
    case 'ADD_COMMENT_REACTION':
    case 'REMOVE_COMMENT_REACTION':
      return updateKnownThread(threads, event.payload.threadId, (thread) => ({
        ...thread,
        comments: thread.comments.map((comment) =>
          comment.id === event.payload.commentId
            ? applyReaction(
                comment,
                event.payload.emoji,
                event.payload.user,
                event.type === 'ADD_COMMENT_REACTION',
              )
            : comment,
        ),
      }));
  }
}

/** Applies `update` to a known thread; unknown threads leave the map untouched. */
function updateKnownThread(
  threads: CommentThreadMap,
  threadId: string,
  update: (thread: CommentThread) => CommentThread,
): CommentThreadMap {
  const thread = threads[threadId];
  return thread === undefined ? threads : upsertThread(threads, update(thread));
}

/** Replaces text and system attributes of one comment when the event carries them. */
function patchComment(
  comments: Comment[],
  commentId: string | undefined,
  changes: {
    text: string | undefined;
    systemAttributes: Comment['systemAttributes'] | undefined;
  },
): Comment[] {
  if (commentId === undefined) {
    return comments;
  }
  return comments.map((comment) =>
    comment.id === commentId
      ? {
          ...comment,
          text: changes.text ?? comment.text,
          systemAttributes:
            changes.systemAttributes ?? comment.systemAttributes,
        }
      : comment,
  );
}

function applyCommentCreated(
  threads: CommentThreadMap,
  payload: CommentCreatedPayload,
  parent: CommentParent,
): CommentThreadMap {
  const existing = threads[payload.threadId];
  if (existing !== undefined) {
    return upsertThread(threads, upsertComment(existing, payload.comment));
  }
  const { parentType, parentId } = payload;
  if (parentType !== parent.type || parentId !== parent.id) {
    return threads;
  }
  return upsertThread(threads, {
    id: payload.threadId,
    parentType,
    parentId,
    coordinates: payload.coordinates ?? { x: 0, y: 0 },
    ...(payload.graphicId === undefined
      ? {}
      : { graphicId: payload.graphicId }),
    resolved: payload.resolved ?? false,
    ...(payload.resolvedAt === undefined
      ? {}
      : { resolvedAt: payload.resolvedAt }),
    comments: [payload.comment],
    systemAttributes:
      payload.threadSystemAttributes ?? payload.comment.systemAttributes,
  });
}

function applyCommentsDeleted(
  threads: CommentThreadMap,
  payload: CommentsDeletedPayload,
): CommentThreadMap {
  if (payload.deleteThread) {
    return removeThreads(threads, [payload.threadId]);
  }
  const deleted = new Set<string>(payload.commentIds);
  return updateKnownThread(threads, payload.threadId, (thread) => ({
    ...thread,
    comments: thread.comments.filter((comment) => !deleted.has(comment.id)),
  }));
}

function applyReaction(
  comment: Comment,
  emoji: string,
  user: CommentUser,
  isAdd: boolean,
): Comment {
  const reactions: CommentReaction[] = comment.reactions
    .map((reaction) => {
      if (reaction.emoji !== emoji) {
        return reaction;
      }
      const users = reaction.users.filter((u) => u.id !== user.id);
      return { ...reaction, users: isAdd ? [...users, user] : users };
    })
    .filter((reaction) => reaction.users.length > 0);
  const hasEmoji = comment.reactions.some(
    (reaction) => reaction.emoji === emoji,
  );
  if (isAdd && !hasEmoji) {
    reactions.push({ emoji, users: [user] });
  }
  return { ...comment, reactions };
}
